/**
 * Recipes Index Page
 *
 * Lists all recipes with filtering, sorting, and grid display.
 */

import { Head, Link, usePage } from '@inertiajs/react';
import { Navbar } from '@/components';
import { RecipeCard } from '@/components/food/RecipeCard';
import { RecipeFilters } from '@/components/food/RecipeFilters';
import type { RecipesIndexProps } from '@/types/food';
import type { SharedProps } from '@/types';

type PageProps = RecipesIndexProps & SharedProps;

export default function RecipesIndex() {
  const { recipes, tags, filters } = usePage<PageProps>().props;

  return (
    <>
      <Head title="Recipes" />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="food" />

        <main className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Recipes</h1>
            <Link
              href="/food/recipes/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add Recipe
            </Link>
          </div>

          {/* Filters */}
          <RecipeFilters tags={tags} filters={filters} />

          {/* Recipe Grid or Empty State */}
          {recipes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {recipes.map((recipe) => (
                <RecipeCard key={recipe.id} recipe={recipe} />
              ))}
            </div>
          ) : (
            <EmptyState hasFilters={hasActiveFilters(filters)} />
          )}
        </main>
      </div>
    </>
  );
}

/**
 * Check if any filters are active
 */
function hasActiveFilters(filters: RecipesIndexProps['filters']): boolean {
  return !!(
    filters.search ||
    filters.tag_id ||
    filters.tag_ids?.length ||
    filters.max_time ||
    filters.freezable ||
    filters.eat_immediately
  );
}

/**
 * Empty state component
 */
function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="text-center py-16 px-4 bg-white rounded-xl">
      <div className="mb-4">
        <svg
          className="w-16 h-16 mx-auto text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        {hasFilters ? 'No recipes found' : 'No recipes yet'}
      </h2>
      <p className="text-gray-500 mb-6">
        {hasFilters
          ? 'Try adjusting your filters or search terms.'
          : 'Get started by adding your first recipe.'}
      </p>
      {!hasFilters && (
        <Link
          href="/food/recipes/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Your First Recipe
        </Link>
      )}
    </div>
  );
}
